import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { forexApi, type MasterSignalDetail } from "@/lib/forex-api";
import { stageReasonLabel } from "./mtf-display";

function accountStatus(account: MasterSignalDetail["accounts"][number]): string {
  if (account.result?.phase === "filled" && account.result.order_effect === "filled") return "Broker 成交已回报";
  if (account.command_status === "unknown") return "执行结果待对账";
  if (account.command_status === "received") return "EA 已接收，尚未确认成交";
  if (account.command_status) return `命令 ${account.command_status}`;
  return `目标 ${account.target_state}`;
}

export default function ExecutionDetailPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const signalId = params.get("id") || "";
  const agentId = params.get("agent_id") || "";
  const returnTo = `/forex/execution-detail${agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ""}`;
  const [detail, setDetail] = useState<MasterSignalDetail | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true; setLoading(true); setDetail(null); setError("");
    if (!signalId) { setError("缺少信号编号"); setLoading(false); return; }
    forexApi.getStrategySignal(signalId, agentId || undefined)
      .then((result) => { if (active) setDetail(result); })
      .catch(() => { if (active) setError("信号或账号执行详情不可用，请从列表重新进入"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [signalId, agentId, revision]);

  return <div className="space-y-5">
    <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "共享信号与执行", href: returnTo }, { label: "信号详情" }]} />
    <div className="flex items-center justify-between gap-3"><div><h2 className="text-lg font-semibold">共享信号详情</h2><p className="font-mono text-xs text-text-muted">{signalId}</p></div>
      <button type="button" disabled={loading} onClick={() => setRevision(value => value + 1)} className="text-sm text-brand">刷新</button><button type="button" onClick={() => navigate(returnTo)} className="rounded-lg border px-3 py-2 text-xs">返回列表</button></div>
    {loading && <p className="text-sm text-text-muted">加载中...</p>}
    {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
    {detail && <><section className="rounded-lg border bg-white p-4 text-sm">
      <h3 className="font-semibold">策略信号</h3><p className="mt-2">智能体 {detail.signal_summary.agent_id} · {detail.signal_summary.symbol} · {detail.signal_summary.action}</p>
      <p className="mt-1 text-xs text-text-muted">信号生成只表示策略判断通过，不代表任何账号已下单或成交。</p>
    </section>
    <section aria-label="账号执行结果" className="space-y-3"><h3 className="text-sm font-semibold">逐账号执行结果</h3>
      {!detail.accounts.length && <p className="rounded-lg border bg-white p-4 text-xs text-text-muted">本次无参与执行的账号。</p>}
      {detail.accounts.map((account) => <article key={account.target_id} className="rounded-lg border bg-white p-4 text-xs space-y-2">
        <div className="flex flex-wrap justify-between gap-2"><span className="font-semibold">{account.target_role === "platform_master" ? "平台账号" : "参与账号"} · {account.account_scope}</span><span>{accountStatus(account)}</span></div>
        <p className="text-text-muted">目标 {account.target_id} · 命令 {account.command_id ? <Link className="text-brand underline" to={`/forex/account-commands/detail?id=${encodeURIComponent(account.command_id)}`}>{account.command_id}</Link> : "尚未生成"}</p>
        {account.readiness_reason && <p className="text-amber-700">准入原因：{stageReasonLabel(account.readiness_reason) || account.readiness_reason}</p>}
        {account.result && <p>回报阶段 {account.result.phase || "未知"} · 订单效果 {account.result.order_effect || "未知"}</p>}
      </article>)}
    </section></>}
  </div>;
}
